import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { handle, handleWithFallback } from '@/lib/displayName';

import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import OnboardingTutorial from '@/components/OnboardingTutorial';
import DriverDetailsCard from '@/components/DriverDetailsCard';
import LocationTracker from '@/components/LocationTracker';
import Navbar from '@/components/layout/Navbar';
import MobileTabBar from '@/components/layout/MobileTabBar';
import MobileMenuOverlay from '@/components/layout/MobileMenuOverlay';
import NotificationBell from '@/components/NotificationBell';
import DashboardCustomizer, { getDashboardConfig } from '@/components/DashboardCustomizer';
import usePushNotifications from '@/hooks/usePushNotifications';
import SmartInstallModal from '@/components/SmartInstallModal';
import { SectionTitle } from '@/components/SectionTitle';
import VerifiedBadge from '@/components/VerifiedBadge';
import { HolographicWidget, TerminalWidget, AtmosphericWidget } from '@/components/WeatherWidgets';
import PostItNote from '@/components/PostItNote';
import SendPostItDialog from '@/components/SendPostItDialog';
import { PostTripReviewPrompt } from '@/components/reviews/PostTripReviewPrompt';
import ScrapeInstagramButton from '@/components/admin/ScrapeInstagramButton';
import { 
  MapPin, 
  Calendar, 
  Car,
  Star,
  Clock,
  Users,
  Plus,
  LogOut,
  X,
  XCircle,
  Trash2,
  Share2,
  Loader2,
  PoundSterling,
  Ticket,
  TrendingUp,
  Settings,
  ChevronRight,
  Home,
  Sparkles,
  Navigation,
  PartyPopper,
  MessageSquare,
  Eye,
  Play,
  Bell,
  Menu,
  Music,
  Download,
  AlertTriangle,
  SlidersHorizontal,
  ClipboardList,
  Zap,
  CalendarDays,
  ArrowUpCircle,
  ShoppingBag as ShoppingBagIcon,
  Compass,
  CheckCircle2,
  StickyNote,
  Search,
  User,
  Fuel,
  History,
  Heart,
  Shield,
  HelpCircle,
  Globe,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline component for creating a vendor space
const CreateSpaceButton = ({ user, onCreated }) => {
  const [creating, setCreating] = useState(false);
  const [slug, setSlug] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [spaceType, setSpaceType] = useState('');

  const spaceTypes = [
    { id: 'organiser', label: 'Event Organiser', active: 'bg-purple-500 text-white' },
    { id: 'dj', label: 'SK8 DJ', active: 'bg-cyan-500 text-white' },
    { id: 'merch', label: 'Merch Vendor', active: 'bg-pink-500 text-white' },
    { id: 'teacher', label: 'Teacher', active: 'bg-green-500 text-white' },
    { id: 'skate_og', label: 'Skate OG', active: 'bg-amber-500 text-white' },
    { id: 'other', label: 'Other', active: 'bg-slate-500 text-white' },
  ];

  const handleCreate = async () => {
    if (!slug.trim()) { toast.error('Enter a URL name'); return; }
    if (!spaceType) { toast.error('Pick what best describes you'); return; }
    setCreating(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const handle = user.username ? `@${user.username}` : 'My';
      await axios.post(`${API}/vendor-spaces`, {
        display_name: `${handle} Corner`,
        slug: slug.toLowerCase().replace(/[^a-z0-9-]/g, '-'),
        space_type: spaceType,
        bio: ''
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Your space is live!');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create space');
    } finally {
      setCreating(false);
    }
  };

  if (!showForm) {
    return (
      <Button size="sm" className="rounded-full bg-purple-500 hover:bg-purple-600" onClick={(e) => { e.stopPropagation(); setShowForm(true); }}>
        Create Space
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-2" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap gap-1.5">
        {spaceTypes.map(t => (
          <button
            key={t.id}
            onClick={() => setSpaceType(t.id)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${spaceType === t.id ? t.active : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid={`space-type-${t.id}`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Input
          placeholder="your-name"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          className="h-8 w-32 text-xs"
          autoFocus
        />
        <Button size="sm" className="rounded-full h-8 text-xs" onClick={handleCreate} disabled={creating}>
          {creating ? '...' : 'Go'}
        </Button>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout, updateProfile, refreshUser } = useAuth();
  const { mode } = useTheme();
  const [loading, setLoading] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [events, setEvents] = useState([]);
  const [openRequests, setOpenRequests] = useState([]);
  const [pendingTickets, setPendingTickets] = useState(0);
  const [pendingVerifications, setPendingVerifications] = useState(0);
  const [dismissedAlerts, setDismissedAlerts] = useState(new Set());
  const [unresolvedErrors, setUnresolvedErrors] = useState(0);
  const [pendingUpgradeRequests, setPendingUpgradeRequests] = useState(0);
  const [driverAvailable, setDriverAvailable] = useState(user?.is_driver_available || false);
  const [cancellingId, setCancellingId] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [pendingBookingForNotification, setPendingBookingForNotification] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [crisisZoneVisible, setCrisisZoneVisible] = useState(false);
  const [worldTripsVisible, setWorldTripsVisible] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/crisis-zone-visibility`)
      .then(res => setCrisisZoneVisible(res.data.crisis_zone_visible))
      .catch(() => {});
    axios.get(`${API}/admin/world-trips-visibility`)
      .then(res => setWorldTripsVisible(res.data.world_trips_visible))
      .catch(() => {});
  }, []);
  const [showOnboardingVideo, setShowOnboardingVideo] = useState(false);
  const [showCancelWarning, setShowCancelWarning] = useState(null);
  
  // Beta banner
  const [showBetaBanner, setShowBetaBanner] = useState(() => {
    return localStorage.getItem('rollm8s_beta_banner_dismissed') !== 'true';
  });
  const [betaPopupOpen, setBetaPopupOpen] = useState(() => {
    // Don't show beta popup if the guided tour hasn't been completed yet (tour acts as welcome)
    const tourDone = localStorage.getItem('rollm8s_guided_tour_complete');
    if (!tourDone) return false;
    return localStorage.getItem('rollm8s_beta_popup_dismissed') !== 'true';
  });
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  
  // PWA install state
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  // Hero/promo video state
  const [heroVideoUrl, setHeroVideoUrl] = useState(null);
  const [heroVideoTransform, setHeroVideoTransform] = useState(null);

  // Dashboard customization state
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [dashboardConfig, setDashboardConfig] = useState(getDashboardConfig(user?.user_id));
  const [vendorSpace, setVendorSpace] = useState(null);

  // New personalised feed state
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [activeRides, setActiveRides] = useState([]);
  const [discoverCorners, setDiscoverCorners] = useState([]);
  const [rideSearch, setRideSearch] = useState('');
  const [rideEventFilter, setRideEventFilter] = useState('all');
  const [ridesExpanded, setRidesExpanded] = useState(false);
  const [postItNotes, setPostItNotes] = useState([]);
  const [showSendPostIt, setShowSendPostIt] = useState(false);
  const [newlyListed, setNewlyListed] = useState([]);
  
  // Update config when user changes
  useEffect(() => {
    if (user?.user_id) {
      setDashboardConfig(getDashboardConfig(user.user_id));
    }
  }, [user?.user_id]);
  
  // Helper to check if a card/section is visible
  const isCardVisible = (type, id) => {
    const items = dashboardConfig[type];
    const item = items?.find(i => i.id === id);
    return item?.visible !== false;
  };
  
  // Push notifications hook (disabled)
  const { isSupported, isSubscribed, loading: pushLoading, subscribe } = usePushNotifications();

  // PWA detection
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    setIsStandalone(standalone);
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = () => {
    setShowInstallModal(true);
  };

  useEffect(() => {
    if (user) {
      setDriverAvailable(user.is_driver_available || false);
      fetchData();
      
      // Check if user needs onboarding (don't show if marked "don't show again")
      const onboardingComplete = localStorage.getItem('rollm8s_onboarding_complete');
      const onboardingHidden = localStorage.getItem('rollm8s_onboarding_hidden');
      if (!onboardingComplete && !onboardingHidden) {
        setShowOnboarding(true);
      }
    }
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      if (!token) {
        console.error('No auth token found');
        setLoading(false);
        return;
      }
      const headers = { Authorization: `Bearer ${token}` };
      
      // Fetch bookings (for all users)
      const bookingsRes = await axios.get(`${API}/bookings`, { headers });
      console.log('Bookings fetched:', bookingsRes.data?.length || 0);
      setBookings(bookingsRes.data || []);

      // Fetch trips (for all users - they might have offered rides)
      try {
        const tripsRes = await axios.get(`${API}/trips/my-trips`, { headers });
        setTrips(tripsRes.data || []);
      } catch (e) {
        console.log('No trips found');
        setTrips([]);
      }

      // Fetch events (for all users - they might have created events)
      try {
        const eventsRes = await axios.get(`${API}/events`);
        const userEvents = (eventsRes.data || []).filter(e => e.organiser_id === user.user_id);
        setEvents(userEvents);
      } catch (e) {
        console.log('No events found');
        setEvents([]);
      }

      // Fetch open ride requests (only for admins and users who have been drivers)
      if (user.is_admin || user.has_been_driver) {
        try {
          const openReqRes = await axios.get(`${API}/open-ride-requests/grouped`, { headers });
          setOpenRequests(openReqRes.data || []);
        } catch (e) {
          console.log('Open requests not available');
        }
      }

      // Fetch pending support tickets count (admins only)
      if (user.is_admin || user.role === 'admin') {
        try {
          const ticketsRes = await axios.get(`${API}/support/tickets/pending-count`, { headers });
          setPendingTickets(ticketsRes.data?.count || 0);
        } catch (e) {
          console.log('Pending tickets not available');
        }
        
        // Fetch pending verifications count (admins only)
        try {
          const statsRes = await axios.get(`${API}/admin/stats`, { headers });
          setPendingVerifications(statsRes.data?.pending_verifications || 0);
        } catch (e) {
          console.log('Admin stats not available');
        }

        // Fetch unresolved error reports count (admins only)
        try {
          const errRes = await axios.get(`${API}/error-reports/unresolved-count`, { headers });
          setUnresolvedErrors(errRes.data?.count || 0);
        } catch (e) {
          console.log('Error reports not available');
        }

        // Fetch pending upgrade requests count (admins only)
        try {
          const upgradeRes = await axios.get(`${API}/admin/upgrade-requests`, { headers });
          setPendingUpgradeRequests(Array.isArray(upgradeRes.data) ? upgradeRes.data.length : 0);
        } catch (e) {
          console.log('Upgrade requests not available');
        }
      }

      // Fetch vendor space (for event organisers)
      if (user.is_event_organiser) {
        try {
          const spaceRes = await axios.get(`${API}/vendor-spaces/mine`, { headers });
          setVendorSpace(spaceRes.data || null);
        } catch (e) {
          console.log('No vendor space');
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  // Fetch personalised feed data (upcoming events, active rides, corners)
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Upcoming events (no auth needed)
    axios.get(`${API}/events?upcoming_only=true&limit=8`)
      .then(res => setUpcomingEvents((res.data || []).slice(0, 8)))
      .catch(() => {});

    // Active rides
    axios.get(`${API}/trips?status=active`, { headers })
      .then(res => {
        const rides = (res.data || []).filter(t => {
          const seats = (t.available_seats || 0) - (t.passengers?.length || 0);
          return seats > 0 && t.driver_id !== user.user_id;
        });
        setActiveRides(rides);
      })
      .catch(() => {});

    // Discover corners
    axios.get(`${API}/vendor-spaces/explore`)
      .then(res => setDiscoverCorners(res.data || []))
      .catch(() => {});

    // Fetch Post-it notes
    axios.get(`${API}/postit/mine`, { headers })
      .then(res => setPostItNotes(res.data || []))
      .catch(() => {});

    // Fetch newly listed Sk8 Shop items
    axios.get(`${API}/sk8-shop/newly-listed?limit=4`)
      .then(res => setNewlyListed(res.data || []))
      .catch(() => {});

    // Fetch dashboard promo video URL
    axios.get(`${API}/site-settings`)
      .then(res => {
        const dashUrl = res.data?.dashboard_video_url || res.data?.hero_video_url;
        if (dashUrl) {
          setHeroVideoUrl(dashUrl);
          setHeroVideoTransform(res.data.hero_video_transform || null);
        }
      })
      .catch(() => {});
  }, [user]);

  const toggleAvailability = async () => {
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const response = await axios.put(`${API}/driver/availability`, {}, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverAvailable(response.data.is_driver_available);
      toast.success(response.data.is_driver_available ? 'You are now available' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  // Calculate refund based on time before trip
  const calculateRefund = (booking) => {
    if (!booking?.total_pence) return { refundPence: 0, refundPercent: 0, reason: 'No refund available' };
    
    // Get the pickup/trip time
    const pickupTime = booking.pickup_point?.time || booking.pickup_time;
    const eventDate = booking.event_date || booking.trip_date || booking.date;
    
    let tripDateTime;
    if (eventDate && pickupTime) {
      const datePart = new Date(eventDate).toISOString().split('T')[0];
      tripDateTime = new Date(`${datePart}T${pickupTime}:00`);
    } else if (eventDate) {
      tripDateTime = new Date(eventDate);
    } else if (pickupTime) {
      // If we only have pickup time but no date, give full refund
      return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund (trip date unavailable)' };
    } else {
      // No trip date available - give full refund to be safe
      return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund available' };
    }
    
    const now = new Date();
    const hoursUntilTrip = (tripDateTime - now) / (1000 * 60 * 60);
    
    // Full refund if more than 1hr before trip, no refund if less than 1hr
    if (hoursUntilTrip >= 1) {
      return { refundPence: booking.total_pence, refundPercent: 100, reason: 'Full refund (more than 1 hour before trip)' };
    } else if (hoursUntilTrip > 0) {
      return { refundPence: 0, refundPercent: 0, reason: 'No refund (less than 1 hour before trip)' };
    } else {
      return { refundPence: 0, refundPercent: 0, reason: 'No refund (trip has already started)' };
    }
  };

  const handleCancelBooking = async (bookingId) => {
    setCancellingId(bookingId);
    
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      const res = await axios.put(`${API}/bookings/${bookingId}/cancel`, {}, { 
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Booking cancelled!');
      if (res.data?.deleted) {
        // Unpaid booking — removed entirely, seat goes back
        setBookings(prev => prev.filter(b => b.booking_id !== bookingId));
      } else {
        // Paid booking — keep with cancelled stamp
        setBookings(prev => prev.map(b => 
          b.booking_id === bookingId ? { ...b, status: 'cancelled' } : b
        ));
      }
      setShowCancelWarning(null);
      setSelectedBooking(null);
    } catch (error) {
      console.error("Cancel error:", error);
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancellingId(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Quick start trip for drivers - starts location sharing and trip in one click
  const handleQuickStartTrip = async (booking, e) => {
    e.stopPropagation();
    
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      
      // Start the trip (change status to en_route)
      await axios.post(`${API}/tracking/start-trip`, 
        { booking_id: booking.booking_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Start location sharing
      localStorage.setItem(`location_tracking_${booking.booking_id}`, 'active');
      
      // Update local state
      setBookings(prev => prev.map(b => 
        b.booking_id === booking.booking_id 
          ? { ...b, status: 'en_route' }
          : b
      ));
      
      toast.success('Trip started! Location sharing enabled.');
      
      // Open the manage trip modal
      setSelectedBooking({ ...booking, status: 'en_route' });
    } catch (error) {
      console.error('Quick start error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start trip');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'EEE, MMM d • h:mm a');
    } catch {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-success/20 text-success border-success/30';
      case 'completed': return 'bg-secondary/20 text-secondary border-secondary/30';
      case 'cancelled': return 'bg-destructive/20 text-destructive border-destructive/30';
      default: return 'bg-accent/20 text-accent border-accent/30';
    }
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < Math.round(rating) ? 'fill-accent text-accent' : 'text-muted-foreground'}`} 
      />
    ));
  };

  // Calculate stats
  const completedBookings = bookings.filter(b => b.status === 'completed');
  const totalSpent = bookings.filter(b => b.payment_status === 'paid' && b.rider_id === user?.user_id)
    .reduce((acc, b) => acc + b.total_pence, 0) / 100;
  const totalEarned = bookings.filter(b => b.payment_status === 'paid' && b.driver_id === user?.user_id)
    .reduce((acc, b) => acc + (b.amount_pence - b.service_fee_pence), 0) / 100;

  // Unseen booking alerts
  const seenKey = `rollm8s_seen_bookings_${user?.user_id}`;
  const seenBookings = JSON.parse(localStorage.getItem(seenKey) || '[]');
  const unseenDriverBookings = bookings.filter(b =>
    b.driver_id === user?.user_id && b.status === 'confirmed' && !seenBookings.includes(b.booking_id)
  );
  const unseenPassengerBookings = bookings.filter(b =>
    b.rider_id === user?.user_id && b.status === 'confirmed' && !seenBookings.includes(b.booking_id)
  );

  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      
      {/* Back to Home - integrated in Navbar on desktop, not needed as floating button */}

      {/* Desktop: Use standard Navbar — suppress its built-in
          MobileTabBar because Dashboard mounts its own at the bottom
          of the file for the custom mobile layout. */}
      <div className="hidden md:block">
        <Navbar noMobileTabBar />
      </div>

      {/* Mobile: Slim sticky header — opaque so scrolled content doesn't bleed through */}
      <header data-mobile-nav="true" className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border/50 pt-[calc(env(safe-area-inset-top,0px)+0.5rem)]">
        <div className="flex items-center justify-between px-4 pt-2 pb-2">
          {/* Logo + Greeting — sized to match standard Navbar exactly */}
          <div className="flex items-center gap-2">
            <img 
              src={mode === 'dark' ? '/rollm8s-logo-dark.png' : '/rollm8s-logo-light.png'}
              alt="RollM8s Logo"
              className="w-[4rem] h-[4rem] md:w-[10rem] md:h-[10rem] object-contain flex-shrink-0"
            />
            <span className="font-heading text-[2rem] md:text-4xl tracking-wide">
              <span className="text-foreground">HEY</span>
              <span className="gradient-text">{(user.is_admin || user.role === 'admin') ? 'ADMIN' : user.name?.split(' ')[0].toUpperCase()}</span>
            </span>
          </div>

          {/* Right side buttons */}
          <div className="flex items-center gap-2">
            <NotificationBell />
            {/* Menu button — icon-only, native app feel */}
            <button
              type="button"
              className="w-12 h-12 inline-flex items-center justify-center rounded-full text-white shadow-lg shadow-primary/30 bg-gradient-to-br from-primary to-secondary hover:brightness-110 active:scale-95 transition"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              title="Menu"
              data-testid="dashboard-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-7 h-7" strokeWidth={2.5} /> : <Menu className="w-7 h-7" strokeWidth={2.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Dropdown — unified iOS-style sheet rendered after </header> below */}
      </header>
      <MobileMenuOverlay
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        crisisZoneVisible={crisisZoneVisible}
        isStandalone={isStandalone}
        onInstall={handleInstallApp}
        onLogout={handleLogout}
      />

      {/* Mobile spacer — sized to EXACTLY match the fixed mobile header's
          rendered height so the first content child sits flush directly
          below the navbar with no overlap.

          Navbar height breakdown (must stay in sync if you tweak the header):
            • safe-area-inset-top         (env(...))
            • header pt:                  0.5rem
            • inner flex pt:              0.5rem
            • logo h-[4rem]:              4rem
            • inner flex pb:              0.5rem
            • border-b:                   0.0625rem
            • +1px breathing room (safety)
          ───────────────────────────────────────
            = env(safe-area-inset-top) + 5.625rem  (≈ 90px on default 16px) */}
      <div className="md:hidden" style={{ height: 'calc(env(safe-area-inset-top, 0px) + 5.625rem)' }} />

      {/* Main Content */}
      <main className="pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">

          {/* Beta Test Popup - Hidden */}
          {false && showBetaBanner && betaPopupOpen && (
            <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="beta-popup-overlay">
              <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { setBetaPopupOpen(false); localStorage.setItem('rollm8s_beta_popup_dismissed', 'true'); }} />
              <div className="relative w-full max-w-sm bg-card border border-amber-500/30 rounded-2xl overflow-hidden animate-slide-up shadow-2xl" data-testid="beta-popup">
                <button
                  className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-muted/50 transition-colors"
                  onClick={() => { setBetaPopupOpen(false); localStorage.setItem('rollm8s_beta_popup_dismissed', 'true'); }}
                  data-testid="beta-popup-close"
                >
                  <X className="w-4 h-4 text-amber-400" />
                </button>
                <div className="px-6 pt-6 pb-5 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
                    <AlertTriangle className="w-7 h-7 text-amber-400" />
                  </div>
                  <h2 className="text-lg font-bold text-foreground mb-2">Welcome to the RollM8s Beta!</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    All events, users and content you see are sample data for testing. Have a look around and use the <span className="text-amber-400 font-medium">Give Feedback</span> button to help us make improvements before real world launch.
                  </p>
                </div>
                <div className="px-6 pb-6">
                  <button
                    onClick={() => { setBetaPopupOpen(false); localStorage.setItem('rollm8s_beta_popup_dismissed', 'true'); }}
                    className="w-full h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 font-medium text-sm hover:bg-amber-500/25 transition-colors"
                    data-testid="beta-popup-got-it"
                  >
                    Got it, let me explore!
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Beta Banner (collapsed - shows after popup dismissed) */}
          {false && showBetaBanner && !betaPopupOpen && (
            <div className="mb-4 relative overflow-hidden rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="beta-banner">
              <button
                className="absolute top-2 right-2 p-1 text-amber-400/60 hover:text-amber-400 transition-colors"
                onClick={() => { setShowBetaBanner(false); localStorage.setItem('rollm8s_beta_banner_dismissed', 'true'); }}
                data-testid="beta-banner-dismiss"
              >
                <X className="w-4 h-4" />
              </button>
              <p className="text-sm text-amber-200 pr-6">
                <span className="font-semibold">Beta test mode</span> — look around and use the feedback button to help us improve before launch!
              </p>
            </div>
          )}

          {/* Give Feedback Button - Hidden */}
          {false && (
          <button
            onClick={() => setShowFeedbackPrompt(true)}
            className="mb-6 flex items-center gap-2 px-4 py-2.5 rounded-xl border border-primary/30 bg-primary/10 hover:bg-primary/20 transition-colors w-fit"
            data-testid="give-feedback-btn"
          >
            <ClipboardList className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary">Give Feedback</span>
            <ChevronRight className="w-3.5 h-3.5 text-primary/60" />
          </button>
          )}

          {/* Feedback Confirmation Prompt */}
          {showFeedbackPrompt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="feedback-prompt-overlay">
              <div className="bg-card border border-border/50 rounded-2xl p-6 max-w-sm w-full space-y-4">
                <h3 className="text-lg font-semibold text-foreground">Before you go...</h3>
                <p className="text-sm text-muted-foreground">
                  Have you had a chance to look around the site first? Your feedback is more valuable after exploring the features!
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowFeedbackPrompt(false)}
                    className="flex-1 px-4 py-2.5 rounded-full border border-border/50 text-sm font-medium text-foreground hover:bg-muted/20 transition-colors"
                    data-testid="feedback-go-back-btn"
                  >
                    Go Back to Site
                  </button>
                  <button
                    onClick={() => { logout(); window.location.href = '/questionnaire'; }}
                    className="flex-1 px-4 py-2.5 rounded-full bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                    data-testid="feedback-proceed-btn"
                  >
                    Proceed to Feedback
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* Desktop Header - only show on larger screens */}
          <div className="hidden md:block mb-8">
            <div className="flex items-center gap-4">
              <Link to="/my-profile">
                <Avatar className="w-16 h-16 border-2 border-primary/30 hover:border-primary transition-colors">
                  <AvatarImage src={user.selfie_url || user.picture} />
                  <AvatarFallback className="bg-primary/20 text-primary text-xl font-heading">
                    {user.name?.charAt(0)}
                  </AvatarFallback>
                </Avatar>
              </Link>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl mb-1">
                  Hey, <span className="gradient-text">{user.name?.split(' ')[0]}</span>
                </h1>
                <p className="text-muted-foreground">
                  What do you want to do today?
                </p>
              </div>
            </div>
          </div>

          {/* Post-trip review prompt — shows when a recent completed booking
              needs rating. NOT wrapped in a margin div on purpose: when the
              prompt returns null (no pending rating), it occupies zero space
              and the video below sits flush with the navbar. The prompt's
              own component carries both its top-clearance (mt-24 on mobile)
              AND its bottom margin (mb-6) so the spacing is conditional on
              the card being present. */}
          <PostTripReviewPrompt />

          {/* Complete Your Profile Card - Hidden */}
          {false && (!user?.profile_picture || !user?.theme_color) && (
            <Card 
              className="mb-6 bg-gradient-to-r from-fuchsia-500/10 via-purple-500/10 to-pink-500/10 border-fuchsia-500/30 hover:border-fuchsia-500/50 cursor-pointer transition-all overflow-hidden"
              onClick={() => navigate('/profile')}
              data-testid="complete-profile-card"
            >
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-fuchsia-500/20 to-purple-500/20 flex items-center justify-center flex-shrink-0 border-2 border-dashed border-fuchsia-500/40">
                  {user?.profile_picture ? (
                    <img loading="lazy" src={user.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <Sparkles className="w-7 h-7 text-fuchsia-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg text-fuchsia-300 mb-0.5">Complete Your Profile</h3>
                  <p className="text-sm text-muted-foreground">Add a photo and personalize your theme colors</p>
                </div>
                <ChevronRight className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
              </CardContent>
            </Card>
          )}

          {/* Calendar + Post-it moved below Discover Corners */}

          {/* Flashing Alert for Passenger Requests */}
          {openRequests.length > 0 && (
            <div 
              className="mb-6 animate-pulse cursor-pointer"
              onClick={() => navigate('/open-requests')}
              data-testid="passenger-request-alert"
            >
              <div className="bg-gradient-to-r from-secondary/20 via-primary/20 to-secondary/20 border-2 border-secondary/50 rounded-2xl p-4 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-secondary/10 to-transparent animate-shimmer"></div>
                <div className="relative flex items-center justify-center gap-3">
                  <span className="text-3xl animate-bounce">🚨</span>
                  <div className="text-center">
                    <p className="font-heading text-xl text-secondary uppercase tracking-wide">
                      Passenger Requests Waiting!
                    </p>
                    <p className="text-sm text-secondary/80">
                      {openRequests.reduce((acc, g) => acc + g.total_passengers, 0)} rider(s) need lifts to events • Tap to view
                    </p>
                  </div>
                  <span className="text-3xl animate-bounce" style={{ animationDelay: '0.5s' }}>🚨</span>
                </div>
              </div>
            </div>
          )}

          {/* Admin Alerts Row */}
          {(user.is_admin || user.role === 'admin') && (
            (pendingTickets > 0 && !dismissedAlerts.has('tickets')) || 
            (pendingVerifications > 0 && !dismissedAlerts.has('verifications')) || 
            (unresolvedErrors > 0 && !dismissedAlerts.has('errors')) || 
            (pendingUpgradeRequests > 0 && !dismissedAlerts.has('upgrades'))
          ) && (
            <div className="mb-6 flex flex-wrap items-center justify-center gap-2" data-testid="admin-alerts-row">
              {/* Support Tickets */}
              {pendingTickets > 0 && !dismissedAlerts.has('tickets') && (
                <span className="inline-flex items-center gap-1.5 rounded-full border-2 border-primary/50 animate-blink-vivid">
                  <button 
                    className="inline-flex items-center gap-1.5 pl-4 py-2 text-sm font-semibold text-primary hover:bg-primary/10 transition-colors cursor-pointer"
                    onClick={() => navigate('/admin', { state: { tab: 'support' } })}
                    data-testid="support-tickets-alert"
                  >
                    {pendingTickets} Ticket{pendingTickets !== 1 ? 's' : ''}
                  </button>
                  <button
                    className="pr-2 py-2 text-primary/60 hover:text-primary transition-colors"
                    onClick={(e) => { e.stopPropagation(); setDismissedAlerts(prev => new Set([...prev, 'tickets'])); }}
                    data-testid="dismiss-support-tickets-alert"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}

              {/* Pending Verifications */}
              {pendingVerifications > 0 && !dismissedAlerts.has('verifications') && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-purple-500/10 border-2 border-purple-500/50">
                  <button 
                    className="inline-flex items-center gap-1.5 pl-4 py-2 text-sm font-semibold text-purple-600 dark:text-purple-300 hover:bg-purple-500/20 transition-colors cursor-pointer rounded-l-full"
                    onClick={() => navigate('/admin', { state: { tab: 'verifications' } })}
                    data-testid="pending-verifications-alert"
                  >
                    {pendingVerifications} Verification{pendingVerifications !== 1 ? 's' : ''}
                  </button>
                  <button
                    className="pr-2 py-2 text-purple-400/60 hover:text-purple-300 transition-colors"
                    onClick={(e) => { e.stopPropagation(); setDismissedAlerts(prev => new Set([...prev, 'verifications'])); }}
                    data-testid="dismiss-verifications-alert"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}


              {/* Upgrade Requests */}
              {pendingUpgradeRequests > 0 && !dismissedAlerts.has('upgrades') && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 border-2 border-amber-500/50">
                  <button 
                    className="inline-flex items-center gap-1.5 pl-4 py-2 text-sm font-semibold text-amber-600 dark:text-amber-300 hover:bg-amber-500/20 transition-colors cursor-pointer rounded-l-full"
                    onClick={() => navigate('/admin', { state: { tab: 'verifications' } })}
                    data-testid="pending-upgrades-alert"
                  >
                    <ArrowUpCircle className="w-3.5 h-3.5" /> {pendingUpgradeRequests} Upgrade{pendingUpgradeRequests !== 1 ? 's' : ''}
                  </button>
                  <button
                    className="pr-2 py-2 text-amber-400/60 hover:text-amber-300 transition-colors"
                    onClick={(e) => { e.stopPropagation(); setDismissedAlerts(prev => new Set([...prev, 'upgrades'])); }}
                    data-testid="dismiss-upgrades-alert"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Vendor Space Card moved below Discover Corners */}

          {/* "What do you want to do today?" moved below Discover Corners */}

          {/* NEW Booking Alert Banners — high-visibility */}
          {unseenDriverBookings.length > 0 && unseenDriverBookings.map(b => (
            <div
              key={`alert-d-${b.booking_id}`}
              onClick={() => navigate(`/booking-alert/${b.booking_id}`)}
              className="mb-4 cursor-pointer rounded-2xl border-2 border-green-400 bg-green-500/15 p-5 flex items-center gap-4 hover:bg-green-500/25 transition-all relative overflow-hidden"
              style={{ boxShadow: '0 0 20px rgba(34,197,94,0.3), 0 0 40px rgba(34,197,94,0.1)' }}
              data-testid={`driver-booking-alert-${b.booking_id}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-green-500/10 via-transparent to-green-500/10 animate-shimmer" />
              <div className="relative w-14 h-14 rounded-full bg-green-500/25 border-2 border-green-400/60 flex items-center justify-center flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-green-400/20 animate-ping" />
                <CheckCircle2 className="w-7 h-7 text-green-400" />
              </div>
              <div className="relative flex-1 min-w-0">
                <p className="font-bold text-green-300 text-base">New Booking!</p>
                <p className="text-sm text-foreground/80 truncate">{b.rider_name} booked a seat — <span className="font-medium text-foreground">{b.event_name}</span></p>
              </div>
              <ChevronRight className="relative w-6 h-6 text-green-400 flex-shrink-0" />
            </div>
          ))}

          {unseenPassengerBookings.length > 0 && unseenPassengerBookings.map(b => (
            <div
              key={`alert-p-${b.booking_id}`}
              onClick={() => navigate(`/booking-alert/${b.booking_id}`)}
              className="mb-4 cursor-pointer rounded-2xl border-2 border-primary bg-primary/15 p-5 flex items-center gap-4 hover:bg-primary/25 transition-all relative overflow-hidden"
              style={{ boxShadow: '0 0 20px hsl(var(--primary) / 0.3), 0 0 40px hsl(var(--primary) / 0.1)' }}
              data-testid={`passenger-booking-alert-${b.booking_id}`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-primary/10 animate-shimmer" />
              <div className="relative w-14 h-14 rounded-full bg-primary/25 border-2 border-primary/60 flex items-center justify-center flex-shrink-0">
                <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
                <Car className="w-7 h-7 text-primary" />
              </div>
              <div className="relative flex-1 min-w-0">
                <p className="font-bold text-primary text-base">
                  {b.payment_status === 'paid' ? 'Ride Confirmed!' : 'Ride Request Accepted!'}
                </p>
                <p className="text-sm text-foreground/80 truncate">
                  {b.payment_status === 'paid'
                    ? <>Your ride to <span className="font-medium text-foreground">{b.event_name}</span> is booked</>
                    : 'Tap to view details & make payment'
                  }
                </p>
              </div>
              <ChevronRight className="relative w-6 h-6 text-primary flex-shrink-0" />
            </div>
          ))}

          {/* Persistent Active Bookings Section — always visible at top.
              Shows any in-progress booking (pending, confirmed, en_route,
              passenger_collected) so the driver/rider can jump straight to
              the BookingPage from the dashboard without scrolling to the
              "My Bookings" tab at the very bottom. */}
          {(() => {
            const ACTIVE_STATUSES = ['pending', 'confirmed', 'en_route', 'passenger_collected'];
            const activeBookings = bookings.filter(b => ACTIVE_STATUSES.includes(b.status));
            if (activeBookings.length === 0) return null;
            return (
              <div className="mb-8" data-testid="active-bookings-top">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                  <h2 className="text-lg font-bold tracking-tight">Active Bookings</h2>
                  <Badge className="bg-primary/20 text-primary border-primary/30 text-xs">{activeBookings.length}</Badge>
                </div>
                <div className="space-y-2.5">
                  {activeBookings.map(booking => {
                    const isDriver = booking.driver_id === user?.user_id;
                    return (
                      <div
                        key={`active-top-${booking.booking_id}`}
                        onClick={() => navigate(`/booking/${booking.booking_id}`)}
                        className="cursor-pointer rounded-xl border border-primary/30 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent p-4 flex items-center gap-4 hover:border-primary/60 hover:from-primary/15 transition-all group animate-pulse-border"
                        data-testid={`active-booking-top-${booking.booking_id}`}
                      >
                        <div className="w-11 h-11 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                          {isDriver ? <Users className="w-5 h-5 text-primary" /> : <Car className="w-5 h-5 text-primary" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm truncate">{booking.event_name}</p>
                          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                            {booking.event_date && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(booking.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              {isDriver ? <><Users className="w-3 h-3" />Rider: {handle(booking, 'rider')}</> : <><Car className="w-3 h-3" />Driver: {handle(booking, 'driver')}</>}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {booking.payment_status === 'paid' ? (
                            <span className="text-xs font-semibold text-green-400 bg-green-400/10 px-2.5 py-1 rounded-full border border-green-400/20">{(booking.is_free_ride || booking.total_pence === 0) ? 'Free' : 'Paid'}</span>
                          ) : (
                            <span className="text-xs font-semibold text-amber-400 bg-amber-400/10 px-2.5 py-1 rounded-full border border-amber-400/20">Unpaid</span>
                          )}
                          {!(booking.is_free_ride || booking.total_pence === 0) && (
                            <span className="font-bold text-primary text-sm">£{((booking.total_pence || 0) / 100).toFixed(2)}</span>
                          )}
                          <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {/* ═══════ POST-IT NOTES ═══════ */}
          {postItNotes.length > 0 && (
            <div className="mb-8" data-testid="postit-notes-section">
              <div className="flex flex-wrap gap-5">
                {postItNotes.map(note => (
                  <PostItNote
                    key={note.note_id}
                    note={note}
                    onDismiss={(id) => setPostItNotes(prev => prev.filter(n => n.note_id !== id))}
                    onBlock={(uid) => setPostItNotes(prev => prev.filter(n => n.from_user_id !== uid))}
                  />
                ))}
              </div>
            </div>
          )}

          {/* ═══════ PERSONALISED FEED ═══════ */}

          {/* Account Restriction Banner */}
          {user?.restrictions && (user.restrictions.blocked_from_driving || user.restrictions.blocked_from_passenger || user.restrictions.blocked_from_events || user.restrictions.account_suspended) && (
            <div className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30" data-testid="restriction-banner">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="space-y-1.5 flex-1">
                  <p className="text-sm font-semibold text-red-400">Account Restricted</p>
                  {user.restrictions.restriction_reason && (
                    <p className="text-sm text-red-300/80">{user.restrictions.restriction_reason}</p>
                  )}
                  <ul className="text-xs text-red-300/60 space-y-0.5">
                    {user.restrictions.account_suspended && <li>Your account is suspended</li>}
                    {user.restrictions.blocked_from_driving && <li>You cannot offer rides</li>}
                    {user.restrictions.blocked_from_passenger && <li>You cannot book rides</li>}
                    {user.restrictions.blocked_from_events && <li>You cannot create events</li>}
                  </ul>
                  {user.restrictions.appeal_submitted ? (
                    <p className="text-[10px] text-amber-400/80 mt-2">Your response has been sent — awaiting review</p>
                  ) : (
                    <button
                      className="mt-2 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/20 border border-red-500/30 text-red-300 hover:bg-red-500/30 transition-colors"
                      data-testid="restriction-appeal-btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                          const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/restriction-appeal`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(res.data.message || "Sent to admin");
                          if (refreshUser) refreshUser();
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Failed to send");
                        }
                      }}>
                      I've fixed this — request review
                    </button>
                  )}
                  <p className="text-[10px] text-red-400/50 mt-1">Contact support if you believe this is a mistake</p>
                </div>
              </div>
            </div>
          )}

          {/* Promo Video */}
          {heroVideoUrl && (
            <div className="mb-8 -mx-3 sm:-mx-5" data-testid="dashboard-promo-video">
              <div className="relative aspect-video bg-black/60 rounded-xl overflow-hidden">
                  <video
                    src={heroVideoUrl.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${heroVideoUrl}` : heroVideoUrl}
                    className="w-full h-full object-contain"
                    autoPlay loop muted playsInline controls
                  />
              </div>
            </div>
          )}

          {/* Upcoming Events — Cinema-style horizontal scroll */}
          {upcomingEvents.length > 0 && isCardVisible('sections', 'upcoming-events') && (
            <div className="mb-8" data-testid="upcoming-events-feed">
              <div className="flex items-center justify-between mb-3">
                <SectionTitle>Upcoming Events</SectionTitle>
                <button onClick={() => navigate('/events')} className="text-xs text-primary font-medium hover:underline" data-testid="view-all-events-link">View all</button>
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" style={{ scrollSnapType: 'x mandatory' }}>
                {upcomingEvents.map(ev => {
                  const evDate = ev.date ? new Date(ev.date) : null;
                  return (
                    <div
                      key={ev.event_id}
                      onClick={() => navigate(`/events/${ev.event_id}`)}
                      className="flex-shrink-0 w-[260px] sm:w-[280px] cursor-pointer group"
                      style={{ scrollSnapAlign: 'start' }}
                      data-testid={`event-card-${ev.event_id}`}
                    >
                      {/* Image container */}
                      <div className="relative h-[140px] rounded-xl overflow-hidden mb-2">
                        {ev.banner_url || ev.image_url ? (
                          <img
                            src={ev.banner_url || ev.image_url}
                            alt={ev.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-primary/30 to-accent/20 flex items-center justify-center">
                            <Calendar className="w-10 h-10 text-primary/50" />
                          </div>
                        )}
                        {/* Dark overlay */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                        {/* Date badge */}
                        {evDate && (
                          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm rounded-lg px-2 py-1 text-center leading-none">
                            <div className="text-[10px] text-white/70 uppercase font-medium">{format(evDate, 'MMM')}</div>
                            <div className="text-lg font-bold text-white">{format(evDate, 'd')}</div>
                          </div>
                        )}
                        {/* Rides badge */}
                        {ev.available_drivers > 0 && (
                          <div className="absolute top-2 right-2 bg-green-500/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-1 rounded-full">
                            {ev.available_drivers} ride{ev.available_drivers > 1 ? 's' : ''}
                          </div>
                        )}
                        {/* Event name on image */}
                        <div className="absolute bottom-2 left-2 right-2">
                          <p className="text-white font-semibold text-sm truncate drop-shadow-lg">{ev.name}</p>
                        </div>
                      </div>
                      {/* Location */}
                      {ev.location?.address && (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground pl-1">
                          <MapPin className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{ev.location.address.split(',')[0]}</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Active Rides — Compact pill/chip style with search & filter */}
          {activeRides.length > 0 && isCardVisible('sections', 'active-rides') && (
            <div className="mb-8" data-testid="active-rides-feed">
              <div className="flex items-center justify-between mb-3">
                <SectionTitle>Drivers Available</SectionTitle>
                <span className="text-xs text-muted-foreground">{activeRides.length} ride{activeRides.length !== 1 ? 's' : ''}</span>
              </div>

              {/* Search + filter bar */}
              <div className="mb-3 space-y-2">
                <Input
                  placeholder="Search driver or event..."
                  value={rideSearch}
                  onChange={e => { setRideSearch(e.target.value); setRidesExpanded(true); }}
                  className="h-9 text-sm rounded-full bg-muted/40 border-border/30 px-4"
                  data-testid="rides-search-input"
                />
                {/* Event filter tabs */}
                {(() => {
                  const eventNames = [...new Set(activeRides.map(r => r.event_name).filter(Boolean))];
                  if (eventNames.length <= 1) return null;
                  return (
                    <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-1">
                      <button
                        onClick={() => setRideEventFilter('all')}
                        className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all ${rideEventFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-white hover:bg-muted'}`}
                        data-testid="rides-filter-all"
                      >
                        All
                      </button>
                      {eventNames.map(name => (
                        <button
                          key={name}
                          onClick={() => setRideEventFilter(name)}
                          className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all truncate max-w-[160px] ${rideEventFilter === name ? 'bg-primary text-primary-foreground' : 'bg-muted/50 text-white hover:bg-muted'}`}
                          data-testid={`rides-filter-${name.replace(/\s+/g, '-').toLowerCase()}`}
                        >
                          {name}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Filtered ride list */}
              {(() => {
                let filtered = activeRides;
                if (rideEventFilter !== 'all') {
                  filtered = filtered.filter(r => r.event_name === rideEventFilter);
                }
                if (rideSearch.trim()) {
                  const q = rideSearch.toLowerCase();
                  filtered = filtered.filter(r =>
                    (r.driver_name || '').toLowerCase().includes(q) ||
                    (r.event_name || '').toLowerCase().includes(q)
                  );
                }
                const displayCount = ridesExpanded ? filtered.length : Math.min(3, filtered.length);
                const shown = filtered.slice(0, displayCount);
                const hasMore = filtered.length > displayCount;

                if (filtered.length === 0) {
                  return <p className="text-xs text-muted-foreground text-center py-3">No rides match — try a different search or check back later</p>;
                }

                return (
                  <>
                    <div className="flex flex-col gap-2">
                      {shown.map(ride => {
                        const seatsLeft = (ride.available_seats || 0) - (ride.passengers?.length || 0);
                        return (
                          <div
                            key={ride.trip_id}
                            onClick={() => ride.event_id ? navigate(`/events/${ride.event_id}`) : null}
                            className="flex items-center gap-3 p-3 rounded-xl bg-muted/40 hover:bg-muted/70 cursor-pointer transition-colors group"
                            data-testid={`ride-chip-${ride.trip_id}`}
                          >
                            <Avatar className="h-9 w-9 flex-shrink-0 ring-2 ring-background">
                              <AvatarImage src={ride.driver_photo} />
                              <AvatarFallback className="text-xs bg-primary/20 text-primary">{(ride.driver_username || ride.driver_name || '?')[0]?.toUpperCase()}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{handle(ride, 'driver')} <VerifiedBadge show={ride.driver_verified} size={13} /></p>
                              <p className="text-xs text-muted-foreground truncate">{ride.event_name || 'Event ride'}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <span className="text-xs font-semibold text-green-500 bg-green-500/10 px-2 py-0.5 rounded-full">
                                {seatsLeft} seat{seatsLeft !== 1 ? 's' : ''}
                              </span>
                              {ride.price_per_seat > 0 && (
                                <span className="text-xs text-muted-foreground font-medium">
                                  £{(ride.price_per_seat / 100).toFixed(0)}
                                </span>
                              )}
                              <ChevronRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary transition-colors" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {hasMore && (
                      <button
                        onClick={() => setRidesExpanded(true)}
                        className="w-full mt-2 py-2 text-xs text-primary font-medium hover:underline"
                        data-testid="rides-show-more"
                      >
                        Show {filtered.length - displayCount} more ride{filtered.length - displayCount !== 1 ? 's' : ''}
                      </button>
                    )}
                    {ridesExpanded && filtered.length > 4 && (
                      <button
                        onClick={() => setRidesExpanded(false)}
                        className="w-full mt-1 py-1 text-xs text-muted-foreground font-medium hover:underline"
                        data-testid="rides-show-less"
                      >
                        Show less
                      </button>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Discover Corners — Stories-style circular avatars */}
          {discoverCorners.length > 0 && isCardVisible('sections', 'discover-corners') && (
            <div className="mb-8" data-testid="discover-corners-feed">
              <div className="flex items-center justify-between mb-3">
                <SectionTitle>Discover Corners</SectionTitle>
                <button onClick={() => navigate('/rollflix')} className="text-xs text-primary font-medium hover:underline" data-testid="view-all-corners-link">Browse all</button>
              </div>
              <div className="flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
                {discoverCorners.map(corner => {
                  const categoryColors = {
                    organiser: 'from-purple-500 to-fuchsia-500',
                    dj: 'from-cyan-400 to-blue-500',
                    merch: 'from-pink-500 to-rose-500',
                    teacher: 'from-green-400 to-emerald-500',
                    skate_og: 'from-amber-400 to-orange-500',
                    other: 'from-slate-400 to-slate-500',
                  };
                  const ring = categoryColors[corner.space_type] || categoryColors.other;
                  return (
                    <div
                      key={corner.space_id}
                      onClick={() => navigate(`/space/${corner.slug}`)}
                      className="flex flex-col items-center flex-shrink-0 cursor-pointer group w-[72px]"
                      data-testid={`corner-bubble-${corner.space_id}`}
                    >
                      <div className={`p-[2.5px] rounded-full bg-gradient-to-br ${ring} mb-1.5`}>
                        <div className="rounded-full bg-background p-[2px]">
                          <Avatar className="h-14 w-14">
                            <AvatarImage src={corner.logo_url} className="object-cover" />
                            <AvatarFallback className="text-sm font-bold bg-muted">{(corner.display_name || '?')[0]}</AvatarFallback>
                          </Avatar>
                        </div>
                      </div>
                      <span className="text-[11px] text-center text-muted-foreground group-hover:text-foreground transition-colors truncate w-full leading-tight">{corner.display_name}</span>
                      <span className="text-[9px] text-center text-muted-foreground/60 capitalize">{(corner.space_type || '').replace('_', ' ')}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══════ END PERSONALISED FEED ═══════ */}

          {/* Calendar Widget + Post-it overlay */}
          {(() => {
            const today = new Date();
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            const weekEnd = new Date(today);
            weekEnd.setDate(today.getDate() + 6);
            const eventDatesThisWeek = new Set();
            const thisWeekEvents = (upcomingEvents || []).filter(ev => {
              if (!ev.date) return false;
              const evDate = new Date(ev.date);
              return evDate >= new Date(today.getFullYear(), today.getMonth(), today.getDate()) && evDate <= weekEnd;
            });
            thisWeekEvents.forEach(ev => {
              const d = new Date(ev.date);
              eventDatesThisWeek.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
            });
            const days = Array.from({ length: 7 }, (_, i) => {
              const d = new Date(today);
              d.setDate(today.getDate() + i);
              const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              return { day: dayNames[d.getDay()], date: d.getDate(), isToday: i === 0, hasEvent: eventDatesThisWeek.has(key) };
            });
            const nextEvent = thisWeekEvents.length > 0 ? thisWeekEvents[0] : null;
            const secondEvent = thisWeekEvents.length > 1 ? thisWeekEvents[1] : null;
            return (
              <div className="relative mb-6">
                <button
                  type="button"
                  onClick={() => navigate('/calendar')}
                  className="relative overflow-hidden rounded-3xl p-5 w-full text-left hover:scale-[1.01] active:scale-[0.99] transition-transform"
                  style={{
                    background: 'linear-gradient(135deg, #1e0b3b, #120B29)',
                    border: '1px solid rgba(255,255,255,0.06)',
                    boxShadow: '0 8px 32px rgba(217,70,239,0.12)',
                  }}
                  data-testid="dashboard-calendar-widget"
                >
                  <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-cyan-500/15 blur-3xl" />
                  <div className="absolute -bottom-6 -left-6 w-24 h-24 rounded-full bg-fuchsia-500/15 blur-3xl" />
                  <div className="relative z-10 flex items-center gap-4">
                    <div className="flex flex-col items-center min-w-[70px]">
                      <span className="text-[10px] text-cyan-400 uppercase tracking-[0.2em] font-bold">{dayNames[today.getDay()]}</span>
                      <span className="text-5xl font-black text-white leading-none drop-shadow-lg">{today.getDate()}</span>
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest">{monthNames[today.getMonth()]}</span>
                    </div>
                    <div className="w-px h-16 bg-gradient-to-b from-transparent via-cyan-400/30 to-transparent" />
                    <div className="flex-1 space-y-2 pr-28">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-cyan-400 shadow-[0_0_8px_rgba(34,211,238,0.8)]" />
                        <span className="text-xs text-zinc-300">{thisWeekEvents.length} event{thisWeekEvents.length !== 1 ? 's' : ''} this week</span>
                      </div>
                      {nextEvent ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-fuchsia-500 shadow-[0_0_8px_rgba(217,70,239,0.8)]" />
                          <span className="text-xs text-zinc-300 truncate">{nextEvent.name}</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-fuchsia-500/40" />
                          <span className="text-xs text-zinc-500 italic">No upcoming events</span>
                        </div>
                      )}
                      {secondEvent ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-purple-400" />
                          <span className="text-xs text-zinc-500 truncate">Next: {secondEvent.name}</span>
                        </div>
                      ) : upcomingEvents.length > thisWeekEvents.length ? (
                        <div className="flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-purple-400" />
                          <span className="text-xs text-zinc-500 truncate">Next: {upcomingEvents.find(e => !thisWeekEvents.includes(e))?.name || 'More soon'}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex justify-center gap-3 mt-4 pt-3 border-t border-white/5 relative z-10">
                    {days.map((d, i) => (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <span className={`text-[8px] ${d.isToday ? 'text-cyan-400' : 'text-zinc-600'}`}>{d.day[0]}</span>
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${d.isToday ? 'bg-cyan-400/20 text-cyan-400 ring-1 ring-cyan-400/40' : d.hasEvent ? 'bg-fuchsia-500/15 text-fuchsia-400' : 'text-zinc-600'}`}>
                          {d.date}
                        </div>
                      </div>
                    ))}
                  </div>
                </button>
                <div className="absolute -top-2 -right-2 z-20">
                  <button
                    onClick={() => setShowSendPostIt(true)}
                    className="relative w-32 h-32 transition-transform hover:scale-105 active:scale-95"
                    style={{ transform: 'rotate(4deg)' }}
                    data-testid="send-postit-fab"
                  >
                    <img src="/assets/postit/yellow.webp" alt="" className="w-full h-full object-contain drop-shadow-lg" draggable={false} />
                    <span
                      className="absolute inset-0 flex items-center justify-center text-gray-900 font-bold leading-tight pt-3 px-5"
                      style={{ fontFamily: "'Caveat', cursive", fontSize: '21px' }}
                    >
                      Send a<br/>Post-it
                    </span>
                  </button>
                </div>
              </div>
            );
          })()}

          {/* Newly Listed in Community Listings */}
          {newlyListed.length > 0 && (
            <div className="mb-6" data-testid="newly-listed-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <ShoppingBagIcon className="w-4 h-4 text-primary" />
                  <h3 className="text-sm font-semibold tracking-wide uppercase text-muted-foreground">Newly Listed</h3>
                </div>
                <Link to="/sk8-shop" className="text-[10px] text-primary hover:underline flex items-center gap-1">
                  View Listings <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {newlyListed.map(item => (
                  <Link
                    key={item.listing_id}
                    to="/sk8-shop"
                    className="group rounded-xl overflow-hidden bg-card/60 border border-border/20 hover:border-primary/30 transition-all"
                    data-testid={`newly-listed-${item.listing_id}`}
                  >
                    <div className="aspect-square overflow-hidden bg-muted/20">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBagIcon className="w-8 h-8 text-muted-foreground/20" />
                        </div>
                      )}
                    </div>
                    <div className="p-2">
                      <p className="text-[11px] font-medium leading-tight line-clamp-1">{item.title}</p>
                      <div className="mt-1">
                        {item.price != null ? (
                          <span className="text-primary font-bold text-sm">{'\u00A3'}{item.price.toFixed(2)}</span>
                        ) : item.listing_type === 'free' ? (
                          <span className="text-pink-400 text-[10px]">Free</span>
                        ) : (
                          <span className="text-purple-400 text-[10px]">Swap</span>
                        )}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* What do you want to do today? + Toggle */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-muted-foreground text-sm">What do you want to do today?</p>
            <button
              onClick={() => setShowCustomizer(true)}
              className="w-8 h-8 rounded-lg bg-violet-400/15 border border-violet-400/30 flex items-center justify-center hover:bg-violet-400/25 transition-colors"
              data-testid="customize-dashboard-icon"
              title="Customize Dashboard"
            >
              <SlidersHorizontal className="w-4 h-4 text-violet-400" />
            </button>
          </div>

          {/* My Corner Card */}
          {(user?.is_event_organiser || user?.is_dj) && (
            <div className="mb-4">
              <Card 
                className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 border-purple-400/30 hover:border-purple-400/60 cursor-pointer transition-all hover:scale-[1.01]"
                onClick={() => vendorSpace ? navigate('/my-page') : null}
                data-testid="my-page-card"
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4 md:items-center">
                    <div className="w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-purple-400/20 flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-6 h-6 md:w-7 md:h-7 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-lg">My Corner</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">
                        {vendorSpace 
                          ? 'Customise your page, add merch & promote your events' 
                          : 'Create your own corner of RollM8s to promote your events, merch or general style.'}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {vendorSpace ? (
                        <Button size="sm" className="rounded-full" onClick={(e) => { e.stopPropagation(); navigate('/my-page'); }}>
                          Edit Corner
                        </Button>
                      ) : (
                        <CreateSpaceButton user={user} onCreated={() => { fetchData(); navigate('/my-page'); }} />
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* My Sales card */}
              {vendorSpace && (
                <Card 
                  className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-400/30 hover:border-emerald-400/60 cursor-pointer transition-all hover:scale-[1.01] mt-3"
                  onClick={() => navigate('/my-sales')}
                  data-testid="my-sales-card"
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-11 h-11 rounded-2xl bg-emerald-400/20 flex items-center justify-center flex-shrink-0">
                        <ShoppingBagIcon className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-base">My Sales</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">View orders, shipping details & manage fulfilment</p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full text-xs border-emerald-400/40 hover:bg-emerald-400/10" onClick={(e) => { e.stopPropagation(); navigate('/my-sales'); }}>
                        View
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Action Cards - What do you want to do today? */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="action-cards">
            {dashboardConfig.actionCards
              .filter(card => card.visible !== false)
              .filter(card => {
                if (card.id === 'customize-dashboard') return false; /* Moved to icon above */
                if (card.id === 'download-app' && isStandalone) return false;
                if (card.id === 'trip-catalogue' || card.id === 'admin-panel') return user.is_admin || user.role === 'admin';
                return true;
              })
              .map(card => {
                // Card configurations
                const cardConfig = {
                  'customize-dashboard': {
                    gradient: 'from-violet-500/10 to-violet-500/5',
                    border: 'border-violet-400/30 hover:border-violet-400/60',
                    iconBg: 'bg-violet-400/20',
                    iconColor: 'text-violet-400',
                    icon: SlidersHorizontal,
                    title: 'Customize Dashboard',
                    description: 'Show or hide cards',
                    onClick: () => setShowCustomizer(true),
                  },
                  'my-tickets': {
                    gradient: 'from-amber-500/10 to-amber-500/5',
                    border: 'border-amber-400/30 hover:border-amber-400/60',
                    iconBg: 'bg-amber-400/20',
                    iconColor: 'text-amber-400',
                    icon: Ticket,
                    title: 'Ticket Wallet',
                    description: 'View purchased tickets',
                    onClick: () => navigate('/my-tickets'),
                  },
                  'admin-panel': {
                    gradient: 'from-gray-500/10 to-gray-500/5',
                    border: 'border-gray-400/30 hover:border-gray-400/60',
                    iconBg: 'bg-gray-400/20',
                    iconColor: 'text-gray-400',
                    icon: Settings,
                    title: 'Admin Panel',
                    description: 'Settings, users & reports',
                    onClick: () => navigate('/admin'),
                    adminOnly: true,
                  },
                  'trip-catalogue': {
                    gradient: 'from-fuchsia-500/10 to-fuchsia-500/5',
                    border: 'border-fuchsia-500/30 hover:border-fuchsia-500/60',
                    iconBg: 'bg-fuchsia-500/20',
                    iconColor: 'text-fuchsia-500',
                    icon: ClipboardList,
                    title: 'Trip Catalogue',
                    description: 'View all rides & requests',
                    onClick: () => navigate('/admin/manage-trips'),
                    adminOnly: true,
                  },
                  'download-app': {
                    gradient: 'from-green-500/10 to-green-500/5',
                    border: 'border-green-500/30 hover:border-green-500/60',
                    iconBg: 'bg-green-500/20',
                    iconColor: 'text-green-500',
                    icon: Download,
                    title: 'Download App',
                    description: 'Install for quick access',
                    onClick: handleInstallApp,
                  },
                  'find-ride': {
                    gradient: 'from-primary/10 to-primary/5',
                    border: 'border-primary/30 hover:border-primary/60',
                    iconBg: 'bg-primary/20',
                    iconColor: 'text-primary',
                    icon: Calendar,
                    title: 'Find an Event',
                    description: 'Browse events & book rides',
                    onClick: () => navigate('/events'),
                  },
                  'request-ride': {
                    gradient: 'from-secondary/10 to-secondary/5',
                    border: 'border-secondary/30 hover:border-secondary/60',
                    iconBg: 'bg-secondary/20',
                    iconColor: 'text-secondary',
                    icon: Navigation,
                    title: 'Request a Ride',
                    description: 'Event not listed? Ask for a lift!',
                    onClick: () => navigate('/request-ride'),
                  },
                  'offer-ride': {
                    gradient: 'from-accent/10 to-accent/5',
                    border: 'border-accent/30 hover:border-accent/60',
                    iconBg: 'bg-accent/20',
                    iconColor: 'text-accent',
                    icon: Car,
                    title: 'Offer a Ride',
                    description: 'Driving to an event? Share your car',
                    onClick: () => navigate('/create-trip'),
                  },
                  'create-event': {
                    gradient: 'from-primary/10 to-primary/5',
                    border: 'border-primary/30 hover:border-primary/60',
                    iconBg: 'bg-primary/20',
                    iconColor: 'text-primary',
                    icon: PartyPopper,
                    title: 'Upload Flyer / Create Event',
                    description: 'List your event for free',
                    onClick: () => navigate('/create-event'),
                  },
                  'meet-community': {
                    gradient: 'from-pink-500/10 to-fuchsia-500/5',
                    border: 'border-pink-400/30 hover:border-pink-400/60',
                    iconBg: 'bg-pink-400/20',
                    iconColor: 'text-pink-400',
                    icon: Play,
                    title: 'Meet the Community',
                    description: 'Discover corners on RollFlix',
                    onClick: () => navigate('/rollflix'),
                  },
                  'safe-space': {
                    gradient: 'from-teal-500/10 to-cyan-500/5',
                    border: 'border-teal-400/30 hover:border-teal-400/60',
                    iconBg: 'bg-teal-400/20',
                    iconColor: 'text-teal-400',
                    icon: Home,
                    title: 'Safe Space',
                    description: 'Anonymous mental health corner',
                    onClick: () => navigate('/safe-space'),
                  },
                  'sk8-shop': {
                    gradient: 'from-orange-500/10 to-amber-500/5',
                    border: 'border-orange-400/30 hover:border-orange-400/60',
                    iconBg: 'bg-orange-400/20',
                    iconColor: 'text-orange-400',
                    icon: ShoppingBagIcon,
                    title: 'Community Listings',
                    description: 'Buy, sell & swap skate gear',
                    onClick: () => navigate('/sk8-shop'),
                  },
                  'ranting-spot': {
                    gradient: 'from-red-500/10 to-rose-500/5',
                    border: 'border-red-400/30 hover:border-red-400/60',
                    iconBg: 'bg-red-400/20',
                    iconColor: 'text-red-400',
                    icon: MessageSquare,
                    title: 'Unfiltered',
                    description: 'Got something to vent about?',
                    onClick: () => navigate('/ranting-spot'),
                  },
                  'floor-tax': {
                    gradient: 'from-amber-600/10 to-yellow-600/5',
                    border: 'border-amber-500/30 hover:border-amber-500/60',
                    iconBg: 'bg-amber-500/20',
                    iconColor: 'text-amber-500',
                    icon: AlertTriangle,
                    title: 'Floor Tax',
                    description: 'Fell? Pay the floor tax!',
                    onClick: () => navigate('/floor-tax'),
                  },
                };

                const config = cardConfig[card.id];
                if (!config) return null;
                const IconComponent = config.icon;

                return (
                  <Card 
                    key={card.id}
                    className={`bg-gradient-to-br ${config.gradient} ${config.border} cursor-pointer transition-all hover:scale-[1.02]`}
                    onClick={config.onClick}
                    data-testid={`action-${card.id}`}
                  >
                    <CardContent className="p-6 text-center">
                      <div className={`w-14 h-14 rounded-2xl ${config.iconBg} flex items-center justify-center mx-auto mb-4`}>
                        <IconComponent className={`w-7 h-7 ${config.iconColor}`} />
                      </div>
                      <h3 className="font-semibold text-lg mb-1">{config.title}</h3>
                      <p className="text-sm text-muted-foreground">{config.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
          </div>

          {/* Stats — Sleek inline strip */}
          {isCardVisible('sections', 'stats') && (
            <div className="flex items-center gap-0 rounded-xl bg-muted/30 border border-border/30 mb-8 overflow-hidden divide-x divide-border/30" data-testid="stats-strip">
              <div className="flex-1 flex items-center gap-2 px-4 py-3">
                <Ticket className="w-4 h-4 text-primary flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold leading-none">{completedBookings.length}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Completed</p>
                </div>
              </div>
              <div className="flex-1 flex items-center gap-2 px-4 py-3">
                <Star className="w-4 h-4 text-accent flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold leading-none">{user.average_rating?.toFixed(1) || '5.0'}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Rating</p>
                </div>
              </div>
              {user.role === 'rider' && (
                <div className="flex-1 flex items-center gap-2 px-4 py-3">
                  <PoundSterling className="w-4 h-4 text-secondary flex-shrink-0" />
                  <div>
                    <p className="text-lg font-bold leading-none">£{totalSpent.toFixed(0)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Spent</p>
                  </div>
                </div>
              )}
              {user.role === 'driver' && (
                <div className="flex-1 flex items-center gap-2 px-4 py-3">
                  <TrendingUp className="w-4 h-4 text-success flex-shrink-0" />
                  <div>
                    <p className="text-lg font-bold leading-none">£{totalEarned.toFixed(0)}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">Earned</p>
                  </div>
                </div>
              )}
              <div className="flex-1 flex items-center gap-2 px-4 py-3">
                <Car className="w-4 h-4 text-warning flex-shrink-0" />
                <div>
                  <p className="text-lg font-bold leading-none">{user.total_trips || 0}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">Trips</p>
                </div>
              </div>
            </div>
          )}

          {/* Open Ride Requests Section - Shows for drivers */}
          {openRequests.length > 0 && isCardVisible('sections', 'open-requests') && (
            <Card className="bg-gradient-to-r from-accent/10 to-secondary/10 border-accent/30 mb-8" data-testid="open-requests-section">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-accent" />
                    Riders Need Lifts!
                  </CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/open-requests')}
                    className="rounded-full"
                    data-testid="view-all-requests-btn"
                  >
                    View All
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <p className="text-sm text-muted-foreground mb-4">
                  {openRequests.reduce((acc, g) => acc + g.total_passengers, 0)} rider(s) looking for lifts to unlisted events
                </p>
                <div className="space-y-3">
                  {openRequests.slice(0, 3).map((group, idx) => (
                    <div 
                      key={idx}
                      className="p-3 rounded-xl bg-background/50 border border-border/30 cursor-pointer hover:border-primary/50 transition-colors"
                      onClick={() => navigate('/open-requests')}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <PartyPopper className="w-4 h-4 text-primary" />
                            <span className="font-medium truncate">{group.event_name}</span>
                          </div>
                          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {group.event_location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {new Date(group.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                            </span>
                          </div>
                        </div>
                        <Badge variant="secondary" className="shrink-0">
                          <Users className="w-3 h-3 mr-1" />
                          {group.total_passengers}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
                {openRequests.length > 3 && (
                  <p className="text-sm text-muted-foreground text-center mt-3">
                    +{openRequests.length - 3} more events with riders waiting
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-primary" />
            </div>
          ) : isCardVisible('sections', 'my-bookings') ? (
            <Tabs value={(searchParams.get('tab') || (bookings.length > 0 ? 'bookings' : (user?.has_been_driver || user?.role === 'driver' ? 'trips' : 'events')))} onValueChange={(v) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', v); return p; }, { replace: true })} className="space-y-6">
              <TabsList className="bg-card border border-border/50 p-1 rounded-full h-auto inline-flex max-w-full overflow-x-auto no-scrollbar">
                <TabsTrigger value="bookings" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white text-xs whitespace-nowrap px-3 py-1.5" data-testid="tab-bookings">
                  Bookings {bookings.length > 0 && `(${bookings.length})`}
                </TabsTrigger>
                {(user?.has_been_driver || user?.role === 'driver') && (
                  <TabsTrigger value="trips" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white text-xs whitespace-nowrap px-3 py-1.5">
                    Trips
                  </TabsTrigger>
                )}
                <TabsTrigger value="events" className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-white text-xs whitespace-nowrap px-3 py-1.5">
                  My Events
                </TabsTrigger>
              </TabsList>

              {/* My Bookings Tab */}
              <TabsContent value="bookings" className="space-y-4">
                {bookings.length === 0 ? (
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-8 text-center">
                      <Ticket className="w-12 h-12 mx-auto text-primary/30 mb-3" />
                      <p className="text-base font-medium mb-1">No bookings yet</p>
                      <p className="text-sm text-muted-foreground mb-4">Find an event and book a ride — your first adventure starts here</p>
                      <Button onClick={() => navigate('/events')} className="rounded-full bg-primary hover:bg-primary/90">
                        Browse Events
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-3">
                    {[...bookings].sort((a, b) => (a.status === 'cancelled') - (b.status === 'cancelled')).map(booking => (
                      <Card 
                        key={booking.booking_id} 
                        className={`bg-card border-border/50 cursor-pointer hover:border-primary/50 transition-all ${booking.status === 'cancelled' ? 'opacity-70' : ''}`}
                        onClick={() => navigate(`/booking/${booking.booking_id}`)}
                        data-testid={`dashboard-booking-${booking.booking_id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-semibold truncate">{booking.event_name}</span>
                                <Badge className={`text-xs shrink-0 ${getStatusColor(booking.status)}`}>{booking.status}</Badge>
                              </div>
                              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                                {booking.event_date && (
                                  <span className="flex items-center gap-1">
                                    <Calendar className="w-3 h-3" />
                                    {new Date(booking.event_date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </span>
                                )}
                                {booking.pickup_point?.time && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {booking.pickup_point?.time_type === 'after' ? `After ${booking.pickup_point.time}` : booking.pickup_point.time}
                                  </span>
                                )}
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {booking.rider_id === user?.user_id ? `Driver: ${handle(booking, 'driver')}` : `Rider: ${handle(booking, 'rider')}`}
                                </span>
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <p className="font-bold text-primary">£{((booking.total_pence || 0) / 100).toFixed(2)}</p>
                              {booking.payment_status === 'paid' && (
                                <span className="text-xs text-green-500">Paid</span>
                              )}
                              {booking.status === 'cancelled' && (
                                <button
                                  className="block text-xs text-red-400 hover:text-red-300 mt-1"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    try {
                                      const t = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                                      await axios.delete(`${API}/bookings/${booking.booking_id}`, { headers: { Authorization: `Bearer ${t}` } });
                                      toast.success('Booking removed');
                                      setBookings(prev => prev.filter(b => b.booking_id !== booking.booking_id));
                                    } catch { toast.error('Failed to remove'); }
                                  }}
                                  data-testid={`dashboard-delete-booking-${booking.booking_id}`}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                    <div className="text-center pt-2">
                      <Button variant="outline" onClick={() => navigate('/calendar')} className="rounded-full text-sm">
                        <CalendarDays className="w-4 h-4 mr-2" />
                        View in Calendar
                      </Button>
                    </div>
                  </div>
                )}
              </TabsContent>

              {(user?.has_been_driver || user?.role === 'driver') && (
                <TabsContent value="trips" className="space-y-4">
                  {trips.length === 0 ? (
                    <Card className="bg-card border-border/50">
                      <CardContent className="p-8 text-center">
                        <Car className="w-12 h-12 mx-auto text-primary/30 mb-3" />
                        <p className="text-base font-medium mb-1">No trips created yet</p>
                        <p className="text-sm text-muted-foreground mb-4">Got a car and heading to an event? Offer seats and split the fuel cost</p>
                        <Button 
                          onClick={() => navigate('/create-trip')}
                          className="rounded-full bg-primary hover:bg-primary/90"
                        >
                          Offer a Ride
                        </Button>
                      </CardContent>
                    </Card>
                  ) : (
                    trips.map((trip) => (
                      <Card 
                        key={trip.trip_id} 
                        className="bg-card border-border/50"
                        data-testid={`trip-card-${trip.trip_id}`}
                      >
                        <CardContent className="p-5">
                          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="space-y-2">
                              <div className="flex items-center gap-3">
                                <h3 className="font-semibold text-lg line-clamp-1 break-words">{trip.event_name}</h3>
                                <Badge variant="outline">
                                  {trip.booked_seats || 0}/{trip.available_seats} seats
                                </Badge>
                              </div>
                              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4 text-primary" />
                                  {formatDate(trip.event_date)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4 text-secondary" />
                                  {trip.pickup_points?.length} pickup{trip.pickup_points?.length !== 1 ? 's' : ''}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Badge className={trip.status === 'active' ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'}>
                                {trip.status}
                              </Badge>
                              <Button
                                variant="outline"
                                size="sm"
                                className="rounded-full border-cyan-400/40 text-cyan-400 hover:bg-cyan-400/10 hover:text-cyan-300 gap-1.5 h-8 px-3 whitespace-normal text-left leading-tight"
                                onClick={async () => {
                                  const shareUrl = `${window.location.origin}/api/share/lift/${trip.trip_id}`;
                                  const shareText = `Lift offered to ${trip.event_name} — share the ride, split the fuel.`;
                                  try {
                                    if (navigator.share) {
                                      await navigator.share({ title: `Lift to ${trip.event_name}`, text: shareText, url: shareUrl });
                                    } else {
                                      await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
                                      toast.success('Share link copied');
                                    }
                                  } catch (err) {
                                    if (err?.name !== 'AbortError') {
                                      try {
                                        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
                                        toast.success('Share link copied');
                                      } catch {
                                        toast.error('Could not share');
                                      }
                                    }
                                  }
                                }}
                                data-testid={`share-trip-${trip.trip_id}`}
                                aria-label="Let others know there's a lift available"
                                title="Let others know there's a lift available"
                              >
                                <Share2 className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="text-xs font-medium">Let others know there&apos;s a lift available</span>
                              </Button>
                              {(user?.is_admin || user?.role === 'admin' || trip.driver_id === user?.user_id) && trip.status !== 'cancelled' && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive hover:bg-destructive/10 rounded-full"
                                  onClick={async () => {
                                    const isAdminAction = (user?.is_admin || user?.role === 'admin') && trip.driver_id !== user?.user_id;
                                    const verb = isAdminAction ? 'Delete' : 'Cancel';
                                    const passengerNote = (trip.booked_seats || 0) > 0
                                      ? ` This lift has ${trip.booked_seats} booked passenger${trip.booked_seats === 1 ? '' : 's'} who will be notified and refunded.`
                                      : '';
                                    if (!window.confirm(`${verb} your lift to ${trip.event_name}?${passengerNote}`)) return;
                                    try {
                                      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
                                      await axios.delete(`${API}/trips/${trip.trip_id}`, { headers: { Authorization: `Bearer ${token}` } });
                                      toast.success(isAdminAction ? 'Trip deleted' : 'Lift cancelled');
                                      fetchData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to cancel lift');
                                    }
                                  }}
                                  data-testid={`cancel-trip-${trip.trip_id}`}
                                  title={trip.driver_id === user?.user_id ? "Cancel this lift" : "Delete trip"}
                                  aria-label={trip.driver_id === user?.user_id ? "Cancel this lift" : "Delete trip"}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              )}

              <TabsContent value="events" className="space-y-4">
                {/* Payout Dashboard link for organisers */}
                {user?.is_event_organiser && events.length > 0 && (
                  <Card
                    className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/5 border-emerald-500/20 hover:border-emerald-500/40 cursor-pointer transition-all"
                    onClick={() => navigate('/payouts')}
                    data-testid="payout-dashboard-link"
                  >
                    <CardContent className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                          <PoundSterling className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <p className="font-semibold text-sm">Payout Dashboard</p>
                          <p className="text-xs text-muted-foreground">Track earnings & payout status</p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-muted-foreground" />
                    </CardContent>
                  </Card>
                )}
                {events.length === 0 ? (
                  <Card className="bg-card border-border/50">
                    <CardContent className="p-8 text-center">
                      <Calendar className="w-12 h-12 mx-auto text-primary/30 mb-3" />
                      <p className="text-base font-medium mb-1">No events created yet</p>
                      <p className="text-sm text-muted-foreground mb-4">Running a session, jam or meetup? Get it listed and let the community know</p>
                      <Button 
                        onClick={() => navigate('/create-event')}
                        className="rounded-full bg-primary hover:bg-primary/90"
                      >
                        Upload Flyer / Create Event
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-2 gap-3">
                  {events.map((event) => (
                    <div 
                      key={event.event_id} 
                      className="relative group rounded-xl overflow-hidden cursor-pointer border border-border/30 hover:border-primary/50 transition-all hover:scale-[1.02]"
                      onClick={() => navigate(`/events/${event.event_id}`)}
                      data-testid={`event-card-${event.event_id}`}
                    >
                      {/* Flyer image */}
                      <div className="aspect-[3/4] bg-card">
                        {event.flyer_url ? (
                          <img 
                            src={event.flyer_url} 
                            alt={event.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-primary/20 to-secondary/10">
                            <Calendar className="w-10 h-10 text-primary/40 mb-2" />
                            <span className="text-xs text-muted-foreground px-2 text-center font-medium">{event.name}</span>
                          </div>
                        )}
                      </div>
                      {/* Overlay info at bottom */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-3 pt-8">
                        <h3 className="font-semibold text-sm text-white line-clamp-1">{event.name}</h3>
                        <p className="text-[11px] text-zinc-300 mt-0.5 flex items-center gap-1">
                          <Calendar className="w-3 h-3 shrink-0" />
                          {event.date ? new Date(event.date).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : 'TBC'}
                        </p>
                      </div>
                      {/* Status badge */}
                      {event.status === 'cancelled' && (
                        <div className="absolute top-2 right-2">
                          <Badge className="bg-red-500/90 text-white text-[10px]">Cancelled</Badge>
                        </div>
                      )}
                    </div>
                  ))}
                  </div>
                )}
                </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </main>

      {/* Notification Prompt Modal */}
      {showNotificationPrompt && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-sm w-full p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <Bell className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-semibold text-xl mb-2">Enable Notifications</h2>
            <p className="text-muted-foreground mb-6">
              Get notified when your driver starts the trip so you're ready for pickup!
            </p>
            <div className="flex flex-col gap-3">
              <Button
                onClick={async () => {
                  try {
                    await subscribe();
                    toast.success('Notifications enabled!');
                  } catch (error) {
                    console.error('Failed to enable notifications:', error);
                  }
                  setShowNotificationPrompt(false);
                  if (pendingBookingForNotification) {
                    setSelectedBooking(pendingBookingForNotification);
                    setPendingBookingForNotification(null);
                  }
                }}
                className="w-full bg-primary hover:bg-primary/90"
                disabled={pushLoading}
              >
                {pushLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Enable Notifications
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setShowNotificationPrompt(false);
                  if (pendingBookingForNotification) {
                    setSelectedBooking(pendingBookingForNotification);
                    setPendingBookingForNotification(null);
                  }
                }}
                className="w-full"
              >
                Maybe Later
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Driver Details Dialog */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border/50 flex items-center justify-between">
              <h2 className="font-semibold text-xl">
                {selectedBooking.driver_id === user?.user_id ? 'Manage Trip' : 'Trip Details'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedBooking(null)}
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="p-4">
              <DriverDetailsCard 
                booking={selectedBooking}
                isDriver={selectedBooking.driver_id === user?.user_id}
                showBookingDetails={true}
                onComplete={() => {
                  setSelectedBooking(null);
                  fetchData();
                }}
                onCancel={async (bookingId) => {
                  await handleCancelBooking(bookingId);
                }}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Dashboard Customizer Modal */}
      <DashboardCustomizer 
        open={showCustomizer} 
        onOpenChange={setShowCustomizer}
        onConfigChange={setDashboardConfig}
        userId={user?.user_id}
        isAdmin={user?.is_admin || user?.role === 'admin'}
      />
      <SmartInstallModal
        open={showInstallModal}
        onClose={() => setShowInstallModal(false)}
        deferredPrompt={deferredPrompt}
        onPromptUsed={() => setDeferredPrompt(null)}
      />

      {/* Floating Send Post-it removed - now inline at top */}

      <SendPostItDialog open={showSendPostIt} onOpenChange={setShowSendPostIt} />

      {/* Admin-only floating "Scrape Instagram" FAB — sits above the mobile tab bar */}
      {(user?.is_admin || user?.role === 'admin') && (
        <div className="fixed bottom-20 right-4 z-40" data-testid="admin-scrape-fab">
          <ScrapeInstagramButton
            size="sm"
            className="shadow-lg shadow-fuchsia-900/40 bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
          />
        </div>
      )}

      {/* Mobile bottom tab bar — Dashboard uses a custom header so we mount it here directly */}
      <MobileTabBar onMorePress={() => setMobileMenuOpen(v => !v)} />
    </div>
  );
};

export default DashboardPage;
